/**
 * Product Multipack — client-side interactivity
 * Handles: tab switching, pack selection, hero swap, URL update,
 * subscribe vs one-time toggle, quantity, add to cart.
 */
(function () {
  'use strict';

  function initSection(sectionEl) {
    var configEl = sectionEl.querySelector('[data-multipack-config]');
    if (!configEl) return;

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (err) {
      console.error('[multipack] Bad config JSON', err);
      return;
    }

    var packs = config.packs || [];
    var categories = config.categories || [];
    var product = config.product || {};
    var subscribe = config.subscribeSave || {};

    if (!categories.length) {
      console.warn('[multipack] No category blocks configured.');
      return;
    }

    // The tab the page opens on. Marked per-block so it is independent of
    // position - "All Designs" can sit last in the row and still be where a
    // shopper lands. Falls back to the first tab when nothing is marked.
    var defaultCategory = categories.find(function (c) { return c.default; }) || categories[0];

    var state = {
      activeTab: defaultCategory.slug,
      selectedVariantId: config.selectedVariantId || (packs[0] && packs[0].id),
      buyMode: subscribe.enabled ? 'subscribe' : 'onetime',
      qty: 1,
      imageIndex: 0,
      sellingPlanId: null,
      // Distinguishes "the shopper picked One-Time" from "we forced One-Time
      // because this design has no plans" - only the former should persist.
      choseOneTime: false
    };

    var els = {
      tabs: sectionEl.querySelectorAll('[data-mp-tab]'),
      grid: sectionEl.querySelector('[data-mp-grid]'),
      gallery: sectionEl.querySelector('.mp-gallery'),
      hero: sectionEl.querySelector('[data-mp-hero]'),
      selectedTitle: sectionEl.querySelector('[data-mp-selected-title]'),
      eyebrow: sectionEl.querySelector('[data-mp-eyebrow]'),
      priceSubscribe: sectionEl.querySelector('[data-mp-price-subscribe]'),
      priceOnetime: sectionEl.querySelector('[data-mp-price-onetime]'),
      priceOnetimeDisplay: sectionEl.querySelector('[data-mp-price-onetime-display]'),
      saveLine: sectionEl.querySelector('[data-mp-save-line]'),
      variantIdInput: sectionEl.querySelector('[data-mp-variant-id]'),
      qtyInput: sectionEl.querySelector('[data-mp-qty-input]'),
      qtyButtons: sectionEl.querySelectorAll('[data-mp-qty]'),
      buyOptions: sectionEl.querySelectorAll('[data-mp-buy]'),
      form: sectionEl.querySelector('[data-mp-cart-form]'),
      prev: sectionEl.querySelector('[data-mp-prev]'),
      next: sectionEl.querySelector('[data-mp-next]'),
      cta: sectionEl.querySelector('.mp-cta'),
      thumbs: sectionEl.querySelector('[data-mp-thumbs]'),
      freq: sectionEl.querySelector('[data-mp-freq]'),
      sellingPlanInput: sectionEl.querySelector('[data-mp-selling-plan]'),
      subscribeOption: sectionEl.querySelector('[data-mp-subscribe-option]'),
      subscribeDetails: sectionEl.querySelector('[data-mp-subscribe-details]'),
      cartDrawer: sectionEl.querySelector('[data-mp-cart-drawer]'),
      cartOverlay: sectionEl.querySelector('[data-mp-cart-overlay]'),
      cartItems: sectionEl.querySelector('[data-mp-cart-items]'),
      cartSubtotal: sectionEl.querySelector('[data-mp-cart-subtotal]'),
      cartCloses: sectionEl.querySelectorAll('[data-mp-cart-close]'),
      buyBox: sectionEl.querySelector('[data-mp-cart-form]'),
      sticky: sectionEl.querySelector('[data-mp-sticky]'),
      stickyName: sectionEl.querySelector('[data-mp-sticky-name]'),
      stickyPrice: sectionEl.querySelector('[data-mp-sticky-price]'),
      stickyAdd: sectionEl.querySelector('[data-mp-sticky-add]')
    };

    // --- Helpers ---

    function findPack(predicate) {
      for (var i = 0; i < packs.length; i++) {
        if (predicate(packs[i])) return packs[i];
      }
      return null;
    }

    function findPackById(id) {
      return findPack(function (p) { return p.id === id; });
    }

    function findPackBySku(sku) {
      return findPack(function (p) { return p.sku === sku; });
    }

    function categoryBySlug(slug) {
      return categories.find(function (c) { return c.slug === slug; });
    }

    /* An "All Designs" tab holds everything regardless of tags or metafields,
     * so it keeps working as designs are added without anyone remembering to
     * tag them. Every membership question goes through here so the grid, the
     * empty-tab check and the tab-jump logic cannot disagree. */
    function packInCategory(pack, slug) {
      if (!pack) return false;
      var cat = categoryBySlug(slug);
      if (cat && cat.all) return true;
      return (pack.categories || []).indexOf(slug) !== -1;
    }

    function packsInCategory(slug) {
      return packs.filter(function (p) {
        return packInCategory(p, slug);
      });
    }

    function formatMoney(cents) {
      var dollars = (cents / 100).toFixed(2);
      return '$' + dollars;
    }

    function categoryLabel(slug) {
      var cat = categories.find(function (c) { return c.slug === slug; });
      return cat ? cat.label.toUpperCase() : slug.toUpperCase();
    }

    // --- Renderers ---

    function renderGrid() {
      var visible = packsInCategory(state.activeTab);
      var html = '';
      for (var i = 0; i < visible.length; i++) {
        var p = visible[i];
        var isSelected = p.id === state.selectedVariantId;
        var showBestsellerTag = p.bestseller && state.activeTab !== 'bestsellers';
        html += '<button class="mp-pack' + (isSelected ? ' mp-pack--selected' : '') + '"'
          + ' data-mp-pack-id="' + p.id + '"'
          + ' type="button" aria-pressed="' + isSelected + '">'
          + '<div class="mp-pack__preview">'
          + (p.thumb || p.image
              ? '<img src="' + (p.thumb || p.image) + '" alt="' + escapeAttr(p.title)
                + '" loading="lazy" decoding="async" width="150" height="150">'
              : '')
          + '</div>'
          + '<div class="mp-pack__name">' + escapeHtml(p.title) + '</div>'
          + (showBestsellerTag ? '<div class="mp-pack__bestseller">Bestseller</div>' : '')
          + '</button>';
      }
      els.grid.innerHTML = html;
    }

    // Picking a design changes which tile is outlined and nothing else. Running
    // the full rebuild for that tears down and recreates 60+ <img> elements and
    // repaints the entire grid - which reads as a flicker. Move the outline
    // instead, and leave the tiles alone.
    function renderGridSelection() {
      if (!els.grid) return;
      var tiles = els.grid.querySelectorAll('[data-mp-pack-id]');
      for (var i = 0; i < tiles.length; i++) {
        var tile = tiles[i];
        var selected = parseInt(tile.getAttribute('data-mp-pack-id'), 10) === state.selectedVariantId;
        tile.classList.toggle('mp-pack--selected', selected);
        tile.setAttribute('aria-pressed', selected ? 'true' : 'false');
      }
    }

    function renderHero() {
      var pack = findPackById(state.selectedVariantId);
      if (!pack) return;

      if (els.hero) {
        els.hero.classList.add('is-swapping');
        var img = els.hero.querySelector('img');
        if (!img && pack.image) {
          // Collection mode renders no initial <img> (there is no current
          // product to seed one), so create it rather than silently no-op.
          img = document.createElement('img');
          img.width = 800;
          img.height = 800;
          img.loading = 'eager';
          els.hero.appendChild(img);
        }
        var images = packImages(pack);
        var src = images[state.imageIndex] || pack.image;
        if (img && src) {
          img.src = src;
          img.alt = pack.title;
        }
        // Fade back in after new image loads (or immediately if same src)
        window.setTimeout(function () {
          els.hero.classList.remove('is-swapping');
        }, 60);
      }

      if (els.selectedTitle) {
        els.selectedTitle.textContent = pack.title;
      }
      if (els.eyebrow) {
        // Show the currently-active tab's label (bestseller label wins if this is the bestsellers tab)
        els.eyebrow.textContent = categoryLabel(state.activeTab);
      }
    }

    function packImages(pack) {
      // Fall back to the single featured image when a product has only one.
      var list = (pack && pack.images && pack.images.length) ? pack.images : [];
      if (!list.length && pack && pack.image) list = [pack.image];
      return list.filter(Boolean);
    }

    function renderThumbs() {
      if (!els.thumbs) return;
      var pack = findPackById(state.selectedVariantId);
      var images = packImages(pack);
      // A lone image is not a gallery - show nothing rather than one dead thumb.
      if (!pack || images.length < 2) {
        els.thumbs.innerHTML = '';
        return;
      }
      var html = '';
      for (var i = 0; i < images.length; i++) {
        html += '<button type="button" class="mp-thumb' + (i === state.imageIndex ? ' mp-thumb--active' : '') + '"'
          + ' data-mp-thumb="' + i + '"'
          + ' aria-label="View image ' + (i + 1) + ' of ' + escapeAttr(pack.title) + '">'
          + '<img src="' + images[i] + '" alt="' + escapeAttr(pack.title) + ' view ' + (i + 1) + '" loading="lazy">'
          + '</button>';
      }
      els.thumbs.innerHTML = html;
    }

    function showImage(index) {
      var pack = findPackById(state.selectedVariantId);
      var images = packImages(pack);
      if (!images.length) return;
      state.imageIndex = Math.max(0, Math.min(index, images.length - 1));
      var img = els.hero && els.hero.querySelector('img');
      if (img) img.src = images[state.imageIndex];
      renderThumbs();
    }

    function packPlans(pack) {
      return (pack && pack.selling_plans) || [];
    }

    // Percentage saved for one adjustment, against the one-time price.
    function adjustmentPct(adj, fullPrice) {
      if (!adj || !fullPrice) return null;
      if (adj.value_type === 'percentage') return Math.round(adj.value);
      if (adj.value_type === 'fixed_amount') return Math.round((adj.value / fullPrice) * 100);
      if (adj.value_type === 'price') return Math.round((1 - adj.value / fullPrice) * 100);
      return null;
    }

    // Shopify plans can price the first delivery differently from later ones -
    // a 20%-then-10% plan carries two adjustments. Say so when that is what
    // the plan actually does, and fall back to the allocated price otherwise,
    // so the line never claims a split the plan does not have.
    function savingsText(plan, fullPrice) {
      var adjustments = (plan && plan.adjustments) || [];
      if (adjustments.length > 1) {
        var first = adjustmentPct(adjustments[0], fullPrice);
        var rest = adjustmentPct(adjustments[1], fullPrice);
        if (first !== null && rest !== null && first !== rest) {
          return 'Save ' + first + '% today, then ' + rest + '% on every refill!';
        }
        if (first !== null) return 'Save ' + first + '% on every delivery!';
      }
      if (adjustments.length === 1) {
        var only = adjustmentPct(adjustments[0], fullPrice);
        if (only !== null && only > 0) return 'Save ' + only + '% on every delivery!';
      }
      var pct = fullPrice > 0 ? Math.round((1 - plan.price / fullPrice) * 100) : 0;
      return pct > 0
        ? 'Save ' + pct + '% on every delivery!'
        : 'Delivered on your schedule. Cancel anytime.';
    }

    function selectedPlan() {
      var plans = packPlans(findPackById(state.selectedVariantId));
      for (var i = 0; i < plans.length; i++) {
        if (plans[i].id === state.sellingPlanId) return plans[i];
      }
      return plans[0] || null;
    }

    // Subscribe & Save only exists when Shopify says this design actually has
    // selling plans. Without them the option would advertise a discount the
    // cart cannot honour, so it is hidden rather than shown and ignored.
    function renderSubscribe() {
      var plans = packPlans(findPackById(state.selectedVariantId));
      var available = subscribe.enabled && plans.length > 0;

      if (els.subscribeOption) els.subscribeOption.hidden = !available;
      if (els.subscribeDetails) els.subscribeDetails.hidden = !available;

      if (!available) {
        state.sellingPlanId = null;
        if (state.buyMode === 'subscribe') state.buyMode = 'onetime';
        return;
      }

      // Plans are back and the shopper never opted out, so restore the default.
      if (state.buyMode === 'onetime' && !state.choseOneTime) {
        state.buyMode = 'subscribe';
      }

      var stillValid = false;
      for (var i = 0; i < plans.length; i++) {
        if (plans[i].id === state.sellingPlanId) stillValid = true;
      }
      if (!stillValid) state.sellingPlanId = plans[0].id;

      if (els.freq) {
        var html = '';
        for (var j = 0; j < plans.length; j++) {
          html += '<option value="' + plans[j].id + '">' + escapeHtml(plans[j].name) + '</option>';
        }
        els.freq.innerHTML = html;
        els.freq.value = String(state.sellingPlanId);
      }
    }

    function renderPrices() {
      var pack = findPackById(state.selectedVariantId);
      if (!pack) return;

      var priceCents = pack.price;

      if (els.priceOnetime) {
        els.priceOnetime.textContent = formatMoney(priceCents);
      }
      if (els.priceOnetimeDisplay) {
        els.priceOnetimeDisplay.textContent = formatMoney(priceCents);
      }

      // The subscription price is whatever Shopify allocated for the plan, so
      // the page cannot claim a saving the cart will not apply.
      var plan = selectedPlan();
      if (els.priceSubscribe && plan) {
        els.priceSubscribe.textContent = formatMoney(plan.price);
      }
      if (els.saveLine && plan) {
        els.saveLine.textContent = savingsText(plan, priceCents);
      }

      renderSticky();
    }

    // The sticky bar mirrors the buy box rather than deriving its own figures,
    // so the two can never disagree about what is being bought or for how much.
    function renderSticky() {
      if (!els.sticky) return;
      var pack = findPackById(state.selectedVariantId);
      if (els.stickyName && pack) els.stickyName.textContent = pack.title;
      if (els.stickyPrice) {
        var source = state.buyMode === 'subscribe' ? els.priceSubscribe : els.priceOnetimeDisplay;
        if (source) els.stickyPrice.textContent = source.textContent;
      }
    }

    function renderVariantId() {
      if (els.variantIdInput) {
        els.variantIdInput.value = state.selectedVariantId;
      }
    }

    function renderBuyOption() {
      els.buyOptions.forEach(function (opt) {
        if (opt.getAttribute('data-mp-buy') === state.buyMode) {
          opt.classList.add('mp-buy-option--selected');
        } else {
          opt.classList.remove('mp-buy-option--selected');
        }
      });
    }

    function renderCTA() {
      var pack = findPackById(state.selectedVariantId);
      if (!pack) return;
      if (els.cta) {
        els.cta.disabled = !pack.available;
        els.cta.textContent = pack.available ? 'Add to Bag' : 'Sold Out';
      }
      // The sticky bar must never offer to sell what the buy box says is gone.
      if (els.stickyAdd) {
        els.stickyAdd.disabled = !pack.available;
        els.stickyAdd.textContent = pack.available ? 'Add to Bag' : 'Sold Out';
      }
    }

    function updateURL() {
      if (!window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', state.selectedVariantId);
      window.history.replaceState({}, '', url.toString());
    }

    function renderAll() {
      renderGrid();
      renderHero();
      renderThumbs();
      renderArrows();
      renderSubscribe();
      renderPrices();
      renderVariantId();
      renderBuyOption();
      renderCTA();
      updateURL();
    }

    // --- Handlers ---

    // Everything that has to happen once state.selectedVariantId changes.
    // Both the grid and the tabs route through here: keeping two copies of
    // this list is what left the thumbnails stale when switching tabs.
    function applySelection() {
      ensurePlans(findPackById(state.selectedVariantId));
      state.imageIndex = 0;
      renderHero();
      renderThumbs();
      renderArrows();
      // Plans are per design, so availability has to be re-evaluated here and
      // not only on first render.
      renderSubscribe();
      renderBuyOption();
      renderPrices();
      renderVariantId();
      renderCTA();
      updateURL();
      renderGridSelection();
    }

    function selectVariant(id) {
      if (!id || id === state.selectedVariantId) return;
      if (!findPackById(id)) return;
      state.selectedVariantId = id;
      applySelection();
    }

    // The design a tab opens on: the SKU it names, else the first one it holds.
    function defaultPackFor(slug) {
      var categoryConfig = categoryBySlug(slug);
      var pack = null;
      if (categoryConfig && categoryConfig.default_sku) {
        pack = findPackBySku(categoryConfig.default_sku);
        // A SKU outside the category would open on a design the tab filters out.
        if (!packInCategory(pack, slug)) pack = null;
      }
      return pack || packsInCategory(slug)[0] || null;
    }

    function applyTabDefault(slug) {
      var pack = defaultPackFor(slug);
      if (!pack || pack.id === state.selectedVariantId) return;
      state.selectedVariantId = pack.id;
      applySelection();
    }

    function selectTab(slug) {
      if (slug === state.activeTab) return;
      state.activeTab = slug;
      // Update tab UI
      els.tabs.forEach(function (t) {
        if (t.getAttribute('data-mp-tab') === slug) {
          t.classList.add('mp-tab--active');
        } else {
          t.classList.remove('mp-tab--active');
        }
      });
      var defaultPack = defaultPackFor(slug);
      if (defaultPack) {
        state.selectedVariantId = defaultPack.id;
        // Switching tabs genuinely changes which designs are on screen, so this
        // path does need the full rebuild - applySelection then only has to
        // place the outline within it.
        renderGrid();
        applySelection();
      } else {
        renderGrid();
      }
    }

    function stepImage(direction) {
      // The arrows sit on the hero, so they page through the selected design's
      // photos. Stepping to a different design is what the grid is for.
      var images = packImages(findPackById(state.selectedVariantId));
      if (images.length < 2) return;
      var next = (state.imageIndex + direction + images.length) % images.length;
      showImage(next);
    }

    function renderArrows() {
      // Nothing to page through on a single-photo design.
      var multi = packImages(findPackById(state.selectedVariantId)).length > 1;
      if (els.prev) els.prev.hidden = !multi;
      if (els.next) els.next.hidden = !multi;
    }

    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
    function escapeAttr(s) { return escapeHtml(s); }

    // --- Bringing the picked design back into view ---

    // Below 900px the gallery stops sitting beside the grid and stacks above
    // it, so a shopper who has scrolled down to browse designs taps a tile and
    // sees nothing happen - the hero, the title and the thumbnails all changed
    // several screens above them.
    var STACKED_LAYOUT = '(max-width: 900px)';

    // The theme pins a fixed header once the page is scrolled. Measure whatever
    // is actually pinned rather than hardcoding a height, so the design title
    // does not end up underneath it.
    function pinnedTop() {
      var bottom = 0;
      var candidates = document.querySelectorAll('.header-top-mobile, .header-sticky, header');
      for (var i = 0; i < candidates.length; i++) {
        var el = candidates[i];
        if (window.getComputedStyle(el).position !== 'fixed') continue;
        var rect = el.getBoundingClientRect();
        if (rect.top <= 0 && rect.bottom > bottom) bottom = rect.bottom;
      }
      return bottom;
    }

    // A single scrollTo over a long distance can land short: the distance is
    // measured once, up front, and the page keeps moving underneath it while
    // lazy images resolve. So the scroll checks once, afterwards, whether it
    // actually arrived.
    //
    // The pinned header is measured ONCE, here, and handed to the caller. The
    // theme shows and hides that header as the page moves, so re-measuring it
    // mid-flight makes the target chase the scroll: the page corrects, the
    // header changes, the target moves, it corrects again. That feedback loop
    // is what made the page visibly hop several times before settling.
    var ARRIVED_WITHIN = 24;

    function scrollPageTo(getTarget) {
      var headerOffset = pinnedTop();
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({
        top: getTarget(headerOffset),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
      if (reduceMotion) return;

      // One correction, after the animation has had time to settle. Enough for
      // a page that grew underneath the scroll; not enough to become a chase.
      window.setTimeout(function () {
        var want = getTarget(headerOffset);
        if (Math.abs(window.pageYOffset - want) > ARRIVED_WITHIN) {
          window.scrollTo({ top: want, behavior: 'auto' });
        }
      }, 600);
    }

    function galleryTarget(headerOffset) {
      return Math.max(0, els.gallery.getBoundingClientRect().top + window.pageYOffset - headerOffset - 12);
    }

    function revealGallery() {
      if (!els.gallery) return;
      if (!window.matchMedia(STACKED_LAYOUT).matches) return;

      // Only ever scroll up to it. Someone already looking at the gallery
      // should not have the page yanked around under them.
      if (window.pageYOffset <= galleryTarget(pinnedTop())) return;

      scrollPageTo(galleryTarget);
    }

    // --- Event wiring ---

    els.tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        selectTab(tab.getAttribute('data-mp-tab'));
      });
    });

    els.grid.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-mp-pack-id]');
      if (!btn) return;
      var id = parseInt(btn.getAttribute('data-mp-pack-id'), 10);
      selectVariant(id);
      revealGallery();
    });

    els.buyOptions.forEach(function (opt) {
      opt.addEventListener('click', function () {
        var mode = opt.getAttribute('data-mp-buy');
        if (mode === state.buyMode) return;
        state.buyMode = mode;
        state.choseOneTime = mode === 'onetime';
        renderBuyOption();
        renderPrices();
      });
    });

    els.qtyButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = parseInt(btn.getAttribute('data-mp-qty'), 10);
        var current = parseInt(els.qtyInput.value, 10) || 1;
        var next = Math.max(1, current + step);
        els.qtyInput.value = next;
        state.qty = next;
      });
    });

    if (els.qtyInput) {
      els.qtyInput.addEventListener('change', function () {
        var v = parseInt(els.qtyInput.value, 10);
        if (isNaN(v) || v < 1) v = 1;
        els.qtyInput.value = v;
        state.qty = v;
      });
    }

    if (els.prev) els.prev.addEventListener('click', function () { stepImage(-1); });
    if (els.next) els.next.addEventListener('click', function () { stepImage(1); });

    if (els.freq) {
      els.freq.addEventListener('change', function () {
        state.sellingPlanId = parseInt(els.freq.value, 10) || null;
        renderPrices();
      });
    }

    if (els.thumbs) {
      els.thumbs.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-mp-thumb]');
        if (!btn) return;
        showImage(parseInt(btn.getAttribute('data-mp-thumb'), 10));
      });
    }

    // A tab with no products in it is noise - hide it until its tag is used.
    function hideEmptyTabs() {
      els.tabs.forEach(function (tab) {
        var slug = tab.getAttribute('data-mp-tab');
        tab.hidden = packsInCategory(slug).length === 0;
      });
    }
    hideEmptyTabs();

    // With no catch-all tab, the first category can legitimately be empty.
    // Opening on a hidden tab would show an empty grid, so start on the first
    // tab that actually has something in it.
    if (!packsInCategory(state.activeTab).length) {
      for (var ci = 0; ci < categories.length; ci++) {
        if (packsInCategory(categories[ci].slug).length) {
          selectTab(categories[ci].slug);
          break;
        }
      }
    } else if (config.mode === 'collection') {
      // The tab a page opens on is never clicked, so selectTab never runs for it.
      // Liquid usually seeds the buy panel with the right design already, in which
      // case this is a no-op; it earns its place when the collection is past the
      // 50-product cap and the named design only arrives with the top-up below.
      applyTabDefault(state.activeTab);
    }
    var initialSelectedId = state.selectedVariantId;
    var initialTab = state.activeTab;

    // --- Collections bigger than Liquid will hand over ---

    // `collection.products` returns at most 50 products outside a paginate
    // tag, and does it silently: with 65 designs the 51st onward simply never
    // reaches the page, and whole tabs look empty for no visible reason.
    // When the server-rendered list comes back at exactly that cap, assume it
    // was truncated and fetch the remainder from the storefront JSON endpoint.
    var LIQUID_COLLECTION_CAP = 50;

    function handleize(value) {
      return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }

    // Mirrors the Liquid: strip whichever configured prefix the title starts
    // with, so a topped-up "6 Pack - Blue Haven" reads the same as the ones
    // rendered server-side.
    function stripPackPrefix(title) {
      var out = String(title || '');
      String(config.stripPrefixes || '').split(',').forEach(function (raw) {
        var pre = raw.trim();
        if (pre && out.slice(0, pre.length).toLowerCase() === pre.toLowerCase()) {
          out = out.slice(pre.length);
        }
      });
      return out.trim();
    }

    function categoriesForTags(tags) {
      var handled = (tags || []).map(handleize);
      return categories.filter(function (c) {
        return c.all || !c.tag || handled.indexOf(c.tag) !== -1;
      }).map(function (c) { return c.slug; });
    }

    function money(dollars) {
      var n = parseFloat(dollars);
      return isNaN(n) ? 0 : Math.round(n * 100);
    }

    // products.json hands back raw, full-resolution CDN URLs. The Liquid path
    // sizes everything it emits - 900 for the hero, 300 for the grid tile - so
    // match it here instead of pushing 2000px files into a 150px tile.
    function sized(src, width) {
      if (!src) return null;
      var url = String(src);
      if (url.indexOf('width=') !== -1) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    }

    function packFromJson(p) {
      var v = (p.variants || [])[0];
      if (!v) return null;
      var raw = (p.images || []).slice(0, 8).map(function (img) { return img.src || img; });
      return {
        id: v.id,
        sku: v.sku || '',
        title: stripPackPrefix(p.title),
        handle: p.handle,
        url: '/products/' + p.handle,
        available: v.available !== false,
        price: money(v.price),
        compare_at_price: v.compare_at_price ? money(v.compare_at_price) : null,
        images: raw.map(function (src) { return sized(src, 900); }),
        // Without these two the tile renders no <img> at all and the hero has
        // nothing to fall back to - which is why every design past the 50th
        // came through blank.
        image: sized(raw[0], 900),
        thumb: sized(raw[0], 300),
        categories: categoriesForTags(p.tags),
        bestseller: (p.tags || []).map(handleize).indexOf('bestsellers') !== -1,
        // products.json carries no subscription data, so these are filled in
        // lazily by ensurePlans() if and when the shopper selects the design.
        selling_plans: [],
        needsPlans: true
      };
    }

    function topUpFromCollection() {
      if (config.mode !== 'collection') return;
      if (packs.length < LIQUID_COLLECTION_CAP) return;
      if (!config.collectionHandle) return;

      fetch('/collections/' + config.collectionHandle + '/products.json?limit=250')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data || !data.products) return;
          var known = {};
          packs.forEach(function (p) { known[p.id] = true; });
          var added = 0;
          data.products.forEach(function (raw) {
            var pack = packFromJson(raw);
            if (!pack || known[pack.id]) return;
            known[pack.id] = true;
            packs.push(pack);
            added++;
          });
          if (!added) return;
          hideEmptyTabs();
          // A tab's named design may only have arrived just now. Honour it only if
          // the shopper has not picked anything yet - yanking the hero out from
          // under someone mid-browse would be worse than opening on the wrong design.
          if (state.selectedVariantId === initialSelectedId && state.activeTab === initialTab) {
            applyTabDefault(state.activeTab);
          }
          renderGrid();
        })
        .catch(function () {
          // The first 50 still render. A failed top-up is a smaller page,
          // not a broken one.
        });
    }

    // A topped-up design knows its price but not its subscription plans.
    // Fetch them the first time one is selected, rather than making 15 extra
    // requests up front for designs the shopper may never look at.
    function ensurePlans(pack) {
      if (!pack || !pack.needsPlans) return;
      pack.needsPlans = false;
      fetch('/products/' + pack.handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data) return;
          var plansById = {};
          (data.selling_plan_groups || []).forEach(function (g) {
            (g.selling_plans || []).forEach(function (plan) { plansById[plan.id] = plan; });
          });
          var variant = (data.variants || []).filter(function (v) { return v.id === pack.id; })[0];
          pack.selling_plans = ((variant && variant.selling_plan_allocations) || []).map(function (a) {
            var plan = plansById[a.selling_plan_id] || {};
            return {
              id: a.selling_plan_id,
              name: plan.name || '',
              price: a.price,
              per_delivery_price: a.per_delivery_price || a.price,
              adjustments: (plan.price_adjustments || []).map(function (adj) {
                return {
                  order_count: adj.order_count || 0,
                  value_type: adj.value_type,
                  value: adj.value || 0
                };
              })
            };
          });
          if (state.selectedVariantId !== pack.id) return;
          renderSubscribe();
          renderBuyOption();
          renderPrices();
        })
        .catch(function () { /* design stays one-time only */ });
    }

    topUpFromCollection();

    // --- Cart drawer ---

    /* This page shipped with its own cart drawer, built before the theme's
     * drawer carried the rewards bar, the upsell strip and the free gift
     * badge. Opening it after an add showed the shopper a stripped-down cart
     * on the multipack pages while the header bag icon showed the full one.
     *
     * Hand off to the theme's drawer when it is present so there is one cart
     * everywhere. The local drawer stays as a fallback for the case where the
     * theme's cart module is not on the page. */
    function themeDrawerAvailable() {
      return !!(
        window.jQuery &&
        window.ajaxCart &&
        typeof window.ajaxCart.load === 'function' &&
        document.querySelector('.js-drawer')
      );
    }

    function openCart() {
      if (themeDrawerAvailable()) {
        window.ajaxCart.load();
        window.jQuery('body').trigger('drawer.open');
        return;
      }
      if (!els.cartDrawer) return;
      els.cartDrawer.classList.add('is-open');
      els.cartDrawer.setAttribute('aria-hidden', 'false');
      if (els.cartOverlay) {
        els.cartOverlay.hidden = false;
        // Next frame, so the transition actually runs.
        window.requestAnimationFrame(function () {
          els.cartOverlay.classList.add('is-open');
        });
      }
    }

    function closeCart() {
      if (!els.cartDrawer) return;
      els.cartDrawer.classList.remove('is-open');
      els.cartDrawer.setAttribute('aria-hidden', 'true');
      if (els.cartOverlay) {
        els.cartOverlay.classList.remove('is-open');
        window.setTimeout(function () { els.cartOverlay.hidden = true; }, 250);
      }
    }

    function renderCart(cart) {
      if (!els.cartItems) return;
      if (!cart || !cart.items || !cart.items.length) {
        els.cartItems.innerHTML = '<div class="mp-cart-empty">Your bag is empty.</div>';
        if (els.cartSubtotal) els.cartSubtotal.textContent = formatMoney(0);
        return;
      }
      var html = '';
      for (var i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        var plan = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan;
        html += '<div class="mp-cart-line">'
          + '<img class="mp-cart-line__img" src="' + (it.image || '') + '" alt="' + escapeAttr(it.product_title || '') + '">'
          + '<div>'
          + '<div class="mp-cart-line__name">' + escapeHtml(it.product_title || '') + '</div>'
          + '<div class="mp-cart-line__meta">Qty ' + it.quantity + (plan ? ' &middot; ' + escapeHtml(plan.name) : '') + '</div>'
          + '<button type="button" class="mp-cart-line__remove" data-mp-cart-remove="' + escapeAttr(it.key) + '">Remove</button>'
          + '</div>'
          + '<div class="mp-cart-line__price">' + formatMoney(it.line_price) + '</div>'
          + '</div>';
      }
      els.cartItems.innerHTML = html;
      if (els.cartSubtotal) els.cartSubtotal.textContent = formatMoney(cart.total_price);
    }

    function refreshCart() {
      return window.fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root + 'cart.js' : '/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(renderCart)
        .catch(function (err) { console.error('[multipack] cart fetch failed', err); });
    }

    if (els.cartCloses) {
      els.cartCloses.forEach(function (btn) {
        btn.addEventListener('click', closeCart);
      });
    }
    if (els.cartOverlay) els.cartOverlay.addEventListener('click', closeCart);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeCart();
    });

    if (els.cartItems) {
      els.cartItems.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-mp-cart-remove]');
        if (!btn) return;
        var key = btn.getAttribute('data-mp-cart-remove');
        window.fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity: 0 })
        })
          .then(function (r) { return r.json(); })
          .then(renderCart)
          .catch(function (err) { console.error('[multipack] remove failed', err); });
      });
    }

    // Show the sticky bar only while the real Add to Bag is off screen. A
    // permanently pinned bar would eat a strip of every screen for the whole
    // visit, including the stretch where the button is already right there.
    if (els.sticky && els.cta) {
      if (typeof window.IntersectionObserver === 'function') {
        new window.IntersectionObserver(function (entries) {
          els.sticky.hidden = entries[0].isIntersecting;
        }, { rootMargin: '0px 0px -8px 0px' }).observe(els.cta);
      } else {
        // No observer: better to leave the bar showing than to hide the button
        // behind the whole design grid again.
        els.sticky.hidden = false;
      }
    }

    // The sticky button carries the shopper down to the buy box rather than
    // adding straight to the cart, so the Subscribe & Save / One-Time choice is
    // always made deliberately instead of defaulting past them.
    if (els.stickyAdd && els.buyBox) {
      els.stickyAdd.addEventListener('click', function () {
        var box = els.buyBox;
        scrollPageTo(function (headerOffset) {
          return Math.max(0, box.getBoundingClientRect().top + window.pageYOffset - headerOffset - 16);
        });

        // Tapping "Add to Bag" and merely moving is disorienting on its own, so
        // flag where the shopper has landed and what is being asked of them.
        box.classList.remove('mp-buy-box--flash');
        // Reading offsetWidth restarts the animation when the button is tapped
        // twice; without it the class is re-added in the same frame and nothing
        // replays.
        void box.offsetWidth;
        box.classList.add('mp-buy-box--flash');
        window.setTimeout(function () {
          box.classList.remove('mp-buy-box--flash');
        }, 1600);
      });
    }

    // Add to cart without leaving the page, then reveal the drawer.
    if (els.form) {
      els.form.addEventListener('submit', function (e) {
        e.preventDefault();
        // A selling plan is what makes this a subscription. It must carry the
        // real plan id when subscribing, and must not be submitted at all on a
        // one-time order.
        var subscribing = state.buyMode === 'subscribe' && state.sellingPlanId;
        if (els.sellingPlanInput) {
          els.sellingPlanInput.value = subscribing ? String(state.sellingPlanId) : '';
          els.sellingPlanInput.disabled = !subscribing;
        }
        var data = new FormData(els.form);
        if (els.cta) els.cta.disabled = true;
        window.fetch('/cart/add.js', { method: 'POST', body: data, credentials: 'same-origin' })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'Add to cart failed'); });
            return r.json();
          })
          .then(function () { return refreshCart(); })
          .then(function () { openCart(); })
          .catch(function (err) {
            console.error('[multipack] add to cart failed', err);
            // Fall back to a normal form post so the shopper is never stuck.
            if (els.sellingPlanInput) els.sellingPlanInput.disabled = false;
            els.form.submit();
          })
          .then(function () {
            if (els.cta) els.cta.disabled = false;
            if (els.sellingPlanInput) els.sellingPlanInput.disabled = false;
          });
      });
    }

    refreshCart();

    // Deep-link support: check URL for ?variant=<numeric> OR ?design=<slug>
    var urlParams = new URLSearchParams(window.location.search);
    var initialVariant = parseInt(urlParams.get('variant'), 10);
    var initialDesignSlug = urlParams.get('design');
    if (!initialVariant && initialDesignSlug) {
      var packByHandle = findPack(function (p) { return p.handle === initialDesignSlug; });
      if (packByHandle) initialVariant = packByHandle.id;
    }
    if (initialVariant && findPackById(initialVariant)) {
      state.selectedVariantId = initialVariant;
      // Also switch to a tab that contains this variant if current active tab doesn't
      var currentPack = findPackById(initialVariant);
      if (!packInCategory(currentPack, state.activeTab)) {
        state.activeTab = currentPack.categories[0] || state.activeTab;
        els.tabs.forEach(function (t) {
          if (t.getAttribute('data-mp-tab') === state.activeTab) {
            t.classList.add('mp-tab--active');
          } else {
            t.classList.remove('mp-tab--active');
          }
        });
      }
    }

    renderAll();
  }

  function init() {
    document.querySelectorAll('.multipack-pdp').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init if Shopify section is dynamically rerendered by the theme editor
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('.multipack-pdp');
    if (el) initSection(el);
  });
})();
